import { PantoneBook, type BrandColorPaletteInput } from '../generated/hooks';

/**
 * A palette built outside this package, narrowed to the generated input type.
 *
 * ── Why this exists ──
 *
 * `@codezo/brand-detail` builds the whole-object palette replacement, and it
 * deliberately takes no GraphQL dependency — so it types a Pantone book as a
 * plain `string`. `PantoneBook` is a string ENUM, and TypeScript treats string
 * enums nominally: `'pantoneSolidCoated'` is not assignable to `PantoneBook`
 * even though it is exactly its value. The two types are structurally the same
 * and the compiler will not join them.
 *
 * This bridges them ONCE, here, where the enum actually lives, rather than with
 * a cast at every mutation call site in every app.
 *
 * It re-validates rather than asserting. A book the enum doesn't know would be
 * rejected by the backend anyway — with the whole palette write, not just that
 * colour — so catching it here turns a silent server-side rejection into a
 * named local error.
 */

const BOOKS = new Set<string>(Object.values(PantoneBook));

interface LoosePaletteColor {
	name: string;
	hex: string;
	usage?: string;
	pantone?: { book: string; code: string; hex?: string | null } | null;
}

type LoosePalette = Record<'primary' | 'secondary' | 'accent' | 'neutral', LoosePaletteColor[]>;

export function toBrandColorPaletteInput(palette: LoosePalette): BrandColorPaletteInput {
	const bucket = (colors: LoosePaletteColor[]) =>
		colors.map((color) => ({
			name: color.name,
			hex: color.hex,
			usage: color.usage,
			pantone: color.pantone ? toPantoneRef(color.pantone) : undefined,
		}));

	return {
		primary: bucket(palette.primary),
		secondary: bucket(palette.secondary),
		accent: bucket(palette.accent),
		neutral: bucket(palette.neutral),
	};
}

function toPantoneRef(pantone: { book: string; code: string; hex?: string | null }) {
	if (!BOOKS.has(pantone.book)) {
		throw new Error(`Unknown PANTONE book: ${pantone.book}`);
	}
	return {
		book: pantone.book as PantoneBook,
		code: pantone.code,
		/* Omitted rather than sent: the backend fills in the book's own value,
		 * which is right by definition. */
		hex: pantone.hex ?? undefined,
	};
}
